import { useId } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Globe, Lock } from 'lucide-react';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import { cn } from 'lib/utils';
import {
  DEFAULT_RESOURCE_VISIBILITY,
  RESOURCE_VISIBILITY,
  type ResourceVisibility,
} from 'types/visibility';

type VisibilityFieldProps = {
  value?: ResourceVisibility;
  onValueChange: (value: ResourceVisibility) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
  idPrefix?: string;
  showIcons?: boolean;
};

const VISIBILITY_OPTION_META: Record<
  ResourceVisibility,
  {
    icon: typeof Lock;
    titleKey: string;
    titleFallback: string;
    descriptionKey: string;
    descriptionFallback: string;
  }
> = {
  [RESOURCE_VISIBILITY.PRIVATE]: {
    icon: Lock,
    titleKey: 'PRIVATE',
    titleFallback: 'Private',
    descriptionKey: 'VISIBILITY_PRIVATE_DESCRIPTION',
    descriptionFallback: 'Only you can access this',
  },
  [RESOURCE_VISIBILITY.PUBLIC]: {
    icon: Globe,
    titleKey: 'PUBLIC',
    titleFallback: 'Public',
    descriptionKey: 'VISIBILITY_PUBLIC_DESCRIPTION',
    descriptionFallback: 'Anyone in the project can access this',
  },
};

const labelText = (t: TFunction, key: string, fallback: string) => {
  const value = t(key, { defaultValue: fallback });
  if (!value || value === key || /^[A-Z][A-Z0-9_]+$/.test(value)) {
    return fallback;
  }
  return value;
};

export function VisibilityField({
  value = DEFAULT_RESOURCE_VISIBILITY,
  onValueChange,
  label,
  description,
  disabled = false,
  required = false,
  className,
  orientation = 'horizontal',
  idPrefix,
  showIcons = false,
}: VisibilityFieldProps) {
  const { t } = useTranslation();
  const generatedId = useId();
  const prefix = idPrefix || `visibility-${generatedId}`;
  const fieldLabel = label ?? labelText(t, 'VISIBILITY', 'Visibility');

  return (
    <div className={cn('flex w-full flex-col gap-1', className)}>
      <Label className="text-sm font-medium leading-[1.6] text-grey-700 dark:text-true-white">
        {fieldLabel}
        {required ? <span className="text-error">*</span> : null}
      </Label>
      {description ? (
        <p className="text-xs font-normal leading-[1.5] text-grey-500 dark:text-muted-foreground">
          {description}
        </p>
      ) : null}
      <RadioGroup
        value={value}
        onValueChange={(nextValue) => onValueChange(nextValue as ResourceVisibility)}
        disabled={disabled}
        className={cn(
          'mt-1',
          orientation === 'horizontal'
            ? 'flex flex-col gap-3 sm:flex-row sm:gap-6'
            : 'flex flex-col gap-3'
        )}
        aria-label={fieldLabel}
      >
        {(Object.keys(VISIBILITY_OPTION_META) as ResourceVisibility[]).map((option) => {
          const meta = VISIBILITY_OPTION_META[option];
          const optionId = `${prefix}-${option}`;
          const Icon = meta.icon;

          return (
            <div key={option} className="flex items-start gap-2">
              <RadioGroupItem value={option} id={optionId} className="mt-0.5" />
              <Label htmlFor={optionId} className="flex cursor-pointer flex-col gap-0.5 font-normal">
                <span className="inline-flex items-center gap-1.5 text-sm font-medium leading-[1.4] text-grey-900 dark:text-foreground">
                  {showIcons ? <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
                  {labelText(t, meta.titleKey, meta.titleFallback)}
                </span>
                <span className="text-xs font-normal leading-[1.4] text-grey-500 dark:text-muted-foreground">
                  {/* {t(meta.descriptionKey) || meta.descriptionFallback} */}
                </span>
              </Label>
            </div>
          );
        })}
      </RadioGroup>
    </div>
  );
}

export type { VisibilityFieldProps };
